import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import {
  getIngIdStockNumber,
  GetWellsLabels,
  isNullOrUndefined,
  padZeros,
  GetProteinFormulation,
} from 'utils/Global';
import {
  LayerIngredient,
  ProteinFormulationData,
  WellInfo,
  WellInfoTemp,
} from 'business/models/Layer';
import {
  FragmentLayerDTO,
  IFragmentFragmentScreenDTO,
  IProteinFormulationInfoDTO,
  LayerTypeDTO,
  ProteinLayerDTO,
  SeedInfoDTO,
} from 'business/models/Models';
import { ITabItem } from 'components/tab';
import RMTabs from 'components/customComponents/Controls/RMTabs';
import { IStyle } from 'Styles';
import IngredientWellInfo from './IngredientWellInfo';
import IngredientProteinInfo from './IngredientProteinInfo';
import IngredientAdditiveInfo from './IngredientAdditiveInfo';
import { ReactComponent as IngredientsRect } from 'Icons/Sidebar/IngredientsRect.svg';
import { useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { getIngredient } from 'business/plateviewer/Generate/GenerateCommon';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import SeedService from 'business/services/SeedService';
import { usePrecisionFormatter } from 'CustomHooks/usePrecisionFormatter';
import {
  EPrecisionType,
  PrecisionFormatter,
} from 'components/plateviewer/render/plate/plateviewer/constants';

export const getWellInfoHtml = (ingredients, formatWithPrecision: PrecisionFormatter) => {
  if (!ingredients) {
    return [];
  }
  const html = [];
  ingredients.map((well, index) => {
    const stockData = well.ingredientStock;
    const highPhIngStockData = well.highPhIngStock;
    if (!well) {
      return;
    }
    html.push(
      <div
        id={`ingredientinfo-getwellinfo-container-index-${index}`}
        key={`ingredient-index-${index}`}
        className="wellinfo"
        style={{ display: 'flex' }}
      >
        <div
          id={`ingredientinfo-getwellinfo-ingredientcolor-index-${index}`}
          className="flex ing-icons  flex-space-between flex-y-center w-1/12"
        >
          <div
            id={`ingredientinfo-getwellinfo-ingredientcolor-${well.ingredientColor}-${index}`}
            style={{
              height: '40px',
              width: '8px',
              marginRight: '10px',
              backgroundColor: well.ingredientColor,
            }}
          ></div>

          <IngredientsRect />
        </div>
        <div
          id={`ingredientinfo-getwellinfo-stock-${index}`}
          className="inline marginLeft10"
          style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
          <div id={`ingredientinfo-getwellinfo-stock-${index}-concentration`}>
            <text
              id={`ingredientinfo-getwellinfo-stock-${index}-concentration-value`}
              className="marginRight5"
            >
              {formatWithPrecision(well.concentration, EPrecisionType.Concentration)}
            </text>
            <text
              id={`ingredientinfo-getwellinfo-stock-${index}-concentration-units`}
              className="marginRight8"
            >
              {well.ingredientStock?.concentrationUnits ?? ''}
            </text>
            <text
              id={`ingredientinfo-getwellinfo-stock-${index}-ingredientname`}
              className="marginRight5"
            >
              {well.ingredientName}
            </text>
            <text>({getIngredient(well.ingredientStock?.ingredientId)?.longName})</text>
            {well.pH && (
              <text
                id={`ingredientinfo-getwellinfo-stock-${index}-ph`}
                className="marginLeft8"
              >
                {well.pH ? 'pH ' + formatWithPrecision(well.pH, EPrecisionType.PH) : ''}
              </text>
            )}
            <text
              id={`ingredientinfo-getwellinfo-stock-${index}-ingredienttypename`}
              className="marginLeft4"
            >
              - {well.ingredientTypeName}
            </text>
          </div>

          {stockData && stockData.isBuffer ? (
            <>
              <div
                id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-wrapper`}
                className="ingredient-card-text ing-card-light"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <div id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-data`}>
                  Stock {stockData?.stockConcentration} {stockData?.concentrationUnits},
                  pH {stockData?.stockPh}{' '}
                  {getIngIdStockNumber(stockData.ingredientId, stockData.stockNumber)}
                  {'  '}
                  {well?.ingredientStock?.vendorPartNumber}
                </div>
                <div id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-volume`}>
                  {formatWithPrecision(
                    well.ingredientVolume - well.highPHVolume,
                    EPrecisionType.Volume,
                  )}
                  μL
                </div>
              </div>
              {!highPhIngStockData ? null : (
                <div
                  id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-highph-container`}
                  className="ingredient-card-text ing-card-light"
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-data`}>
                    Stock {highPhIngStockData.stockConcentration}{' '}
                    {highPhIngStockData.concentrationUnits}, pH{' '}
                    {highPhIngStockData.stockPh}{' '}
                    {getIngIdStockNumber(
                      highPhIngStockData.ingredientId,
                      highPhIngStockData.stockNumber,
                    )}
                    {'  '}
                    {well.ingredientStock.vendorPartNumber}
                  </div>
                  <div
                    id={`ingredientinfo-getwellinfo-stockdata-${index}-isbuffer-highphvolume`}
                  >
                    {formatWithPrecision(well.highPHVolume, EPrecisionType.Volume)}μL
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div
                id={`ingredientinfo-getwellinfo-stockdata-${index}-nonbuffer-wrapper`}
                className="ingredient-card-text ing-card-light"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <div id={`ingredientinfo-getwellinfo-stockdata-${index}-nonbuffer-data`}>
                  Stock {stockData?.stockConcentration} {stockData?.concentrationUnits} (
                  {padZeros(stockData?.ingredientId, 4)} -{' '}
                  {padZeros(stockData?.stockNumber, 2)}){'  '}{' '}
                  {well?.ingredientStock?.vendorPartNumber}
                </div>
                <div
                  id={`ingredientinfo-getwellinfo-stockdata-${index}-nonbuffer-ingredientvolume`}
                >
                  {formatWithPrecision(well?.ingredientVolume, EPrecisionType.Volume)}μL
                </div>
              </div>
            </>
          )}
        </div>
      </div>,
    );
  });
  return html;
};

export const getDropVolHTMl = (wellInfo) => {
  const dropVolHTMl = [];
  if (!wellInfo) {
    return [];
  }
  dropVolHTMl.push(
    <div
      id="ingredientinfo-getdropvol-wrapper"
      className="content"
      style={{ width: '100%' }}
    >
      <div
        id="ingredientinfo-getdropvol-dropproteinvolume-container"
        className="row paddingmargin0"
      >
        <div
          id="ingredientinfo-getdropvol-dropproteinvolume-title"
          className="col-md-6 paddingmargin0"
        >
          Drop Protein Volume (µL)
        </div>
        <div
          id="ingredientinfo-getdropvol-dropproteinvolume-data"
          className="col-md-6 paddingmargin0"
        >
          {wellInfo.dropProteinVolume} µL
        </div>
      </div>
      <div className="row paddingmargin0">
        <div
          id="ingredientinfo-getdropvol-dropwellvolume-title"
          className="col-md-6 paddingmargin0"
        >
          Drop Well Volume (µL)
        </div>
        <div
          id="ingredientinfo-getdropvol-dropwellvolume-data"
          className="col-md-6 paddingmargin0"
        >
          {wellInfo.dropWellVolume} µL
        </div>
      </div>
      <br />
    </div>,
  );
  return dropVolHTMl;
};
const getAdditivesHTML = (wellinfo, formatWithPrecision: PrecisionFormatter) => {
  const additivesHTML = [];
  if (wellinfo.additives !== undefined && wellinfo.additives !== null) {
    wellinfo.additives.map((additive) => {
      const stockData = additive.ingredientStock;
      additivesHTML.push(
        <>
          <div
            id={`ingredientinfo-getadditives-${additive.ingredientName}-dropnumber`}
            style={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}
          >
            <div
              id={`ingredientinfo-getadditives-${additive.ingredientName}-dropnumber-title`}
            >
              Drop
            </div>
            <div
              id={`ingredientinfo-getadditives-${additive.ingredientName}-dropnumber-value`}
            >
              Drop {additive.dropNumber}
            </div>
          </div>
          <div
            id={`ingredientinfo-getadditives-${additive.ingredientName}-wrapper`}
            className="wellinfo"
            style={{ display: 'flex' }}
          >
            <div
              id={`ingredientinfo-getadditives-${additive.ingredientName}-ingredientsrect`}
              className="flex  flex-space-between flex-y-center"
            >
              <IngredientsRect />
            </div>
            <div
              id={`ingredientinfo-getadditives-${additive.ingredientName}-container`}
              className="inline marginLeft10"
              style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              <div id={`ingredientinfo-getadditives-${additive.ingredientName}-header`}>
                {formatWithPrecision(
                  parseFloat(additive.concentration),
                  EPrecisionType.Concentration,
                )}{' '}
                {additive.concentrationUnit} {additive.ingredientName} -{' '}
                {additive.ingredientTypeName}
              </div>
              {stockData != undefined ? (
                stockData.isBuffer ? (
                  <div
                    id={`ingredientinfo-getadditives-${additive.ingredientName}-buffer-wrapper`}
                    className="ingredient-card-text ing-card-light"
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <div
                      id={`ingredientinfo-getadditives-${additive.ingredientName}-buffer-data`}
                    >
                      Stock {stockData.stockConcentration} {stockData.concentrationUnits},
                      pH {stockData.stockPh}{' '}
                      {getIngIdStockNumber(stockData.ingredientId, stockData.stockNumber)}
                    </div>
                    <div
                      id={`ingredientinfo-getadditives-${additive.ingredientName}-buffer-volume`}
                    >
                      {formatWithPrecision(
                        parseFloat(additive.ingredientVolume),
                        EPrecisionType.Volume,
                      ) + 'μL'}
                    </div>
                  </div>
                ) : (
                  <div
                    id={`ingredientinfo-getadditives-${additive.ingredientName}-nonbuffer-wrapper`}
                    className="ingredient-card-text ing-card-light"
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <div
                      id={`ingredientinfo-getadditives-${additive.ingredientName}-nonbuffer-data`}
                    >
                      Stock {stockData.stockConcentration} {stockData.concentrationUnits}{' '}
                      (
                      {getIngIdStockNumber(stockData.ingredientId, stockData.stockNumber)}
                    </div>
                    <div
                      id={`ingredientinfo-getadditives-${additive.ingredientName}-nonbuffer-volume`}
                    >
                      {formatWithPrecision(
                        parseFloat(additive.ingredientVolume),
                        EPrecisionType.Volume,
                      ) + 'μL'}
                    </div>
                  </div>
                )
              ) : null}
            </div>
          </div>
        </>,
      );
    });
  }
  return additivesHTML;
};

export default function IngredientInfo() {
  const [wellInfoHTML, setWellInfoHTML] = useState([]);
  const [additivesInfoHTML, setAdditivesInfoHTML] = useState([]);
  const [proteins, setProteins] = useState<ProteinFormulationData[]>([]);
  const [seedInfos, setSeedInfos] = useState<SeedInfoDTO[]>([]);
  const [seedVolumeById, setSeedVolumeById] = useState<Record<number, number>>({});
  const [dropVol, setDropVolume] = useState([]);
  const [fragmentFragentScreens, setFragmentFragentScreens] =
    useState<IFragmentFragmentScreenDTO[]>(null);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const exp = useRMSelector(selectExp);
  const formatWithPrecision = usePrecisionFormatter();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber } = selectedDropImageParameters || {};

  const { well, proteinFormulation } = useMemo(() => {
    if (!exp.plateDto) {
      return { well: null, proteinFormulation: null, plateName: '' };
    }
    let plate = exp.plateDto.find((pl) => pl.plateId === plateID);
    if (plate === null || plate === undefined) {
      plate = exp.plateDto[0];
    }
    const well = !isNullOrUndefined(plate.ingredients)
      ? plate.ingredients.wellList[wellNum - 1]
      : null;
    const proteinFormulation = !isNullOrUndefined(plate.ingredients)
      ? plate.ingredients.proteinFormulation
      : null;
    return { well, proteinFormulation, plateName: plate.plateBarName };
  }, [exp.plateDto, wellNum, plateID]);

  const wellLable = useMemo(() => {
    const labels = GetWellsLabels();
    return labels[wellNum ? wellNum - 1 : 0]?.name || '';
  }, [GetWellsLabels, wellNum]);

  function setWellInfoFromWellLayerList() {
    const wellInfoTemp: WellInfoTemp = {
      proteins: [],
      seedIds: [],
      seedVolumeById: {},
      additives: [],
      ingredients: [],
      proteinInfo: {} as IProteinFormulationInfoDTO,
      wellInfo: {} as WellInfo,
      dropFragmentInfo: [],
    };

    const welllayerIng = [];

    if (well.dropList) {
      well.dropList.map((drop) => {
        if (drop.dropNumber == selectedDropImageParameters?.dropNumber) {
          for (const dropLayer of drop.dropLayerList ?? []) {
            const layer = exp.layers?.find((l) => l.id === dropLayer.layerId);
            if (layer?.type !== LayerTypeDTO.Protein) {
              continue;
            }
            const proteinLayerDetail = layer.layerDetail as ProteinLayerDTO | undefined;
            for (const plpf of proteinLayerDetail?.proteinLayerProteinFormulation ?? []) {
              const pfId = plpf.proteinFormulationId;
              if (!pfId) {
                continue;
              }
              const pfEntry = proteinFormulation?.[pfId];
              if (pfEntry) {
                wellInfoTemp.proteins.push({
                  dropNumber: drop.dropNumber,
                  dropProteinVolume: plpf.startProteinDropVolume,
                  dropWellVolume: plpf.startWellDropVolume,
                  ...pfEntry.proteinInfo,
                  ingredients: pfEntry.ingredients ?? [],
                  standardIngredients: pfEntry.standardIngredients ?? [],
                });
              } else {
                const globalPF = GetProteinFormulation(pfId);
                if (globalPF) {
                  wellInfoTemp.proteins.push({
                    dropNumber: drop.dropNumber,
                    dropProteinVolume: plpf.startProteinDropVolume,
                    dropWellVolume: plpf.startWellDropVolume,
                    name: globalPF.pfName ?? '',
                    proteinLotId: globalPF.proteinLotId ?? '',
                    volume: globalPF.volume ?? 0,
                    storageTemp: globalPF.storageTemp ?? '',
                    pclass: globalPF.Class ?? '',
                    extCoefficient: globalPF.extCoefficient ?? '',
                    datePrepared: globalPF.datePrepared
                      ? new Date(globalPF.datePrepared)
                      : null,
                    ingredients: globalPF.pfIng ?? [],
                    standardIngredients: globalPF.pfStIng ?? [],
                  });
                }
              }
            }
          }
          if (wellInfoTemp.proteins.length === 0 && drop.proteinFormulationID != null) {
            const pfEntry = proteinFormulation?.[drop.proteinFormulationID];
            if (pfEntry) {
              wellInfoTemp.proteins.push({
                dropNumber: drop.dropNumber,
                dropProteinVolume: drop.dropProteinVolume,
                dropWellVolume: drop.dropWellVolume,
                ...pfEntry.proteinInfo,
                ingredients: pfEntry.ingredients ?? [],
                standardIngredients: pfEntry.standardIngredients ?? [],
              });
            } else {
              const globalPF = GetProteinFormulation(drop.proteinFormulationID);
              if (globalPF) {
                wellInfoTemp.proteins.push({
                  dropNumber: drop.dropNumber,
                  dropProteinVolume: drop.dropProteinVolume,
                  dropWellVolume: drop.dropWellVolume,
                  name: globalPF.pfName ?? '',
                  proteinLotId: globalPF.proteinLotId ?? '',
                  volume: globalPF.volume ?? 0,
                  storageTemp: globalPF.storageTemp ?? '',
                  pclass: globalPF.Class ?? '',
                  extCoefficient: globalPF.extCoefficient ?? '',
                  datePrepared: globalPF.datePrepared
                    ? new Date(globalPF.datePrepared)
                    : null,
                  ingredients: globalPF.pfIng ?? [],
                  standardIngredients: globalPF.pfStIng ?? [],
                });
              }
            }
          }

          const seedDropLayers =
            drop.dropLayerList
              ?.map((dl) => ({ dl, layer: exp.layers?.find((l) => l.id === dl.layerId) }))
              .filter(({ layer }) => layer?.type === LayerTypeDTO.Seed) ?? [];
          const derivedSeedIds = seedDropLayers
            .map(({ layer }) => (layer.layerDetail as any).seedId)
            .filter((id): id is number => id != null);
          wellInfoTemp.seedIds = derivedSeedIds.length
            ? derivedSeedIds
            : drop.seedIDs ?? [];

          for (const { dl, layer } of seedDropLayers) {
            const seedId = (layer.layerDetail as any).seedId;
            if (seedId == null) {
              continue;
            }
            wellInfoTemp.seedVolumeById[seedId] =
              (wellInfoTemp.seedVolumeById[seedId] ?? 0) + (dl.volume ?? 0);
          }

          if (wellInfoTemp.wellInfo.dropProteinVolume == null) {
            wellInfoTemp.wellInfo.dropProteinVolume = 0;
          }
          wellInfoTemp.wellInfo.dropProteinVolume += drop.dropProteinVolume;

          if (wellInfoTemp.wellInfo.dropWellVolume == null) {
            wellInfoTemp.wellInfo.dropWellVolume = 0;
          }
          wellInfoTemp.wellInfo.dropWellVolume += drop.dropWellVolume;

          drop.dropLayerList?.forEach((dropLayer) => {
            if (!isNullOrUndefined(dropLayer.dropLayerIngredientList)) {
              dropLayer.dropLayerIngredientList.forEach((dropIngredient) => {
                const layerIngredient: LayerIngredient = {
                  ingredientColor: dropIngredient.hexColor,
                  concentration: dropIngredient.concentration,
                  concentrationUnit: dropIngredient.ingredientStock.concentrationUnits,
                  ingredientVolume: dropIngredient.volume,
                  ingredientTypeName: dropIngredient.ingredientTypeName,
                  ingredientStock: dropIngredient.ingredientStock,
                  dropNumber: drop.dropNumber,
                  ingredientName: dropIngredient.ingredientStockIngredient.longName,
                };
                wellInfoTemp.additives.push({
                  ...layerIngredient,
                  stockConcentration: dropIngredient.ingredientStock.stockConcentration,
                  volume: dropIngredient.volume,
                });
              });
            }

            const layer = exp.layers.find((lyr) => lyr.id === dropLayer.layerId);
            if (layer?.type === LayerTypeDTO.Fragment) {
              const fragmentLayerDetail = layer.layerDetail as FragmentLayerDTO;
              const fragmentScreenId = fragmentLayerDetail.fragmentScreenId;
              // Try to get fragment data from dropLayerFragmentList first (for saved experiments)
              if (dropLayer.dropLayerFragmentList?.length) {
                dropLayer.dropLayerFragmentList.forEach((dlf) => {
                  wellInfoTemp.dropFragmentInfo.push({
                    fragmentId: dlf.fragmentId,
                    fragmentScreenId,
                  });
                });
              } else if (
                fragmentLayerDetail.fragmentScreen?.fragmentFragmentScreen?.length
              ) {
                // Fallback: Get fragment data from layer's fragmentScreen for unsaved experiments
                fragmentLayerDetail.fragmentScreen.fragmentFragmentScreen.forEach(
                  (ffs) => {
                    wellInfoTemp.dropFragmentInfo.push({
                      fragmentId: ffs.fragmentId,
                      fragmentScreenId,
                    });
                  },
                );
              }
            }
          });
        }
      });
    }
    well.wellLayerList.map((wellLayer) => {
      wellLayer.wellLayerIngredientList.map((ingredient) => {
        const layerIngredient: LayerIngredient = new LayerIngredient();
        layerIngredient.ingredientColor = ingredient.hexColor;
        layerIngredient.concentration = ingredient.concentration;
        layerIngredient.concentrationUnit = ingredient.ingredientStock.concentrationUnits;
        layerIngredient.ingredientName = ingredient.ingredientStockIngredient.shortName;
        layerIngredient.ingredientTypeName = ingredient.ingredientTypeName;
        layerIngredient.ingredientStock = ingredient.ingredientStock;
        layerIngredient.ingredientVolume = ingredient.volume;
        layerIngredient.highPhIngStock = ingredient.highPhIngStock;
        layerIngredient.highPHVolume = ingredient.highPHVolume;
        layerIngredient.pH = ingredient?.pH;

        welllayerIng.push(layerIngredient);
      });
    });
    wellInfoTemp.ingredients = welllayerIng;

    return wellInfoTemp;
  }

  useEffect(() => {
    let wellinfo = null;
    if (isNullOrUndefined(well)) {
      return;
    }

    wellinfo = setWellInfoFromWellLayerList();
    //selected drop number assigned if the proteinInfo object is null or {}
    if (!wellinfo.proteinInfo.dropNumber) {
      wellinfo.proteinInfo.dropNumber = dropNumber;
    }

    setProteins(wellinfo.proteins);
    setWellInfoHTML(getWellInfoHtml(wellinfo.ingredients, formatWithPrecision));
    setDropVolume(getDropVolHTMl(wellinfo.wellInfo));
    setAdditivesInfoHTML(getAdditivesHTML(wellinfo, formatWithPrecision));
    setFragmentFragentScreens(wellinfo.dropFragmentInfo);
    setSeedVolumeById(wellinfo.seedVolumeById);
    /**RFR: Need to narrow down the dependencies*/

    const { seedIds } = wellinfo;
    if (seedIds.length) {
      const seedService = new SeedService();
      seedService
        .getSeeds(seedIds)
        .then(setSeedInfos)
        .catch((e) => {
          console.error('Failed to fetch seed info', e);
          setSeedInfos([]);
        });
    } else {
      setSeedInfos([]);
    }
  }, [
    wellLable,
    plateID,
    well,
    proteinFormulation,
    wellNum,
    dropNumber,
    formatWithPrecision,
  ]);

  const isScreenRoute = window.location.pathname.includes('/screen');

  const tabComponents: ITabItem[] = [
    {
      title: 'Well',
      eventKey: 'well',
      content: (
        <IngredientWellInfo
          wellName={wellLable}
          dropVol={dropVol}
          wellInfoHTML={wellInfoHTML}
          wellVolume={well?.wellVolume}
          wellWaterVolume={formatWithPrecision(
            well?.wellWaterVolume,
            EPrecisionType.Volume,
          )}
        />
      ),
    },
    {
      title: 'Protein',
      eventKey: 'protein',
      content: (
        <IngredientProteinInfo
          isScreenRoute={isScreenRoute}
          proteins={proteins}
          seedInfos={seedInfos}
          seedVolumeById={seedVolumeById}
          well={well}
          fragmentIds={fragmentFragentScreens}
          dropNumber={dropNumber}
        />
      ),
    },
    {
      title: 'Additives',
      eventKey: 'additives',
      content: <IngredientAdditiveInfo additivesInfoHTML={additivesInfoHTML} />,
    },
  ];

  const styles: IStyle = {
    container: {
      height: '100%',
    },
  };

  return (
    <>
      <div
        id="ingredientinfo-main-container"
        className="container-fluid p-0"
        style={styles.container}
      >
        <RMTabs
          tabItems={tabComponents}
          defaultActiveKey={'well'}
          id={'IngredientInfoTab'}
        />
      </div>
    </>
  );
}
